import Vue from 'vue';
import {
  getPageInfo,
  getInviteePageInfo,
  addInviteTask,
  addInviteeTask,
  getActivityTaskLog,
  fetchShareCode,
  getXlWxaQRCode
} from './api';
import { resolveComponents, isRiskError } from '@/components/templates/helper';
import actComponentLib from './components/actComponentsLib';

/**
 * 邀请人
 */
const inviterStore = {
  namespaced: true,
  state() {
    return {
      actData: null,
      tplData: null,
      actCode: null,
      metaInfo: null,
      modalConfig: null,
      pinnedBtnComponentObjs: [],
      flowComponentObjs: [],
      positionComponentObjs: [],
      isShowTaskModal: false,
      isShowCouponDetail: false,
      isShowWinModal: false,
      isShowFailModal: false
    };
  },
  actions: {
    async fetchData(_, actCode) {
      Vue.prototype.$hll.showLoading({});
      let actData;
      try {
        actData = await getPageInfo(actCode);
      } catch (error) {
        throw error;
      } finally {
        Vue.prototype.$hll.hideLoading();
      }
      return actData;
    },
    parseTplData(_, actData) {
      let metaInfo = {};
      try {
        metaInfo = JSON.parse(actData.metaInfo);
      } catch (e) {
        console.error('模板数据 parse 出错');
      }
      return metaInfo;
    },
    setPage({ commit }, metaInfo) {
      const inviterPage = metaInfo.pages.find(pageObj => {
        return pageObj.pageName === 'home';
      });
      commit('setTplData', inviterPage);
      return inviterPage;
    },
    setModalConfig({ commit }, metaInfo) {
      // 解析弹窗配置
      const modalPage = metaInfo.pages.find(pageObj => {
        return pageObj.pageName === 'modal';
      });
      if (modalPage && modalPage.modalConfig) {
        commit('setModal', modalPage.modalConfig);
      }
    },
    resolveComponents({ commit }, { actData, inviterPage }) {
      // 解析组件
      const components = inviterPage.components;
      if (!components || !components.length) {
        return;
      }
      // 拿到 metaInfo 之后直接调 helper.resolveComponents 即可生成组件数组用于渲染
      const [
        pinnedBtnComponentObjs, // pinnedBtnComponentObjs 就是右上角固定按钮组，比较特殊，需要单独提取
        flowComponentObjs, // 流式布局的组件数组
        positionComponentObjs // 固定定位的组件数组
      ] = resolveComponents(components, { actData, actComponentLib });
      // 特殊处理助力组件
      const resolvedFlowComponentObjs = flowComponentObjs.map(obj => {
        if (obj.componentTypeId === 1301) {
          return {
            ...obj,
            fullWidth: true
          };
        }
        return obj;
      });
      commit('setComponentObjs', {
        pinnedBtnComponentObjs,
        flowComponentObjs: resolvedFlowComponentObjs,
        positionComponentObjs
      });
      return {
        pinnedBtnComponentObjs,
        flowComponentObjs,
        positionComponentObjs
      };
    },
    initModalCheck({ commit }, actData) {
      const currentTask = actData && actData.inviterTaskInfo;
      if (!currentTask) {
        return;
      }
      // 检查是否有进行中的助力任务
      if (currentTask.userTaskStatus === 'progressing') {
        commit('toggleTaskModal', true);
        return;
      }
      // 是否有未提示的成功的任务
      if (
        currentTask.userTaskStatus === 'success' &&
        currentTask.rewardStatus === 'success' &&
        currentTask.isNotice === 0
      ) {
        commit('toggleWinModal', true);
        return;
      }
      // 是否有未提示的失败的任务
      if (currentTask.userTaskStatus === 'fail' && currentTask.isNotice === 0) {
        commit('toggleFailModal', true);
        return;
      }
    },
    setDefaultAvatar({ commit }, flowComponentObjs) {
      const actComponent = flowComponentObjs.find(obj => {
        return obj.componentTypeId === 1301;
      });
      if (actComponent) {
        commit(
          'app/inviteEarnCoupon/playHistory/setDefaultAvatar',
          {
            defaultAvatarImage: actComponent.componentDetail.defaultAvatarImage
          },
          { root: true }
        );
      }
    },
    async getAndParseActData({ commit, dispatch }, actCode) {
      // 获取数据
      const actData = await dispatch('fetchData', actCode);
      commit('setActData', actData);
      commit('app/inviteEarnCoupon/playHistory/setActId', actData.id, {
        root: true
      });
      // 解析 metaInfo
      const metaInfo = await dispatch('parseTplData', actData);
      commit('setMetaInfo', metaInfo);
      // 解析 inviter page
      const inviterPage = await dispatch('setPage', metaInfo);
      // 设置活动规则
      commit('app/inviteEarnCoupon/setRuleContent', actData.activityRule, {
        root: true
      });
      // 设置弹窗配置
      dispatch('setModalConfig', metaInfo);
      // 解析组件
      const { flowComponentObjs } = await dispatch('resolveComponents', {
        actData,
        inviterPage
      });
      // 设置默认头像（活动记录页面用）
      dispatch('setDefaultAvatar', flowComponentObjs);
      // 设置title
      dispatch('setTitle', inviterPage);
      // 初始弹窗 check
      setTimeout(() => {
        dispatch('initModalCheck', actData);
      }, 500);
    },
    async refreshTaskInfo({ commit, dispatch, state }) {
      // 获取数据
      const actData = await dispatch('fetchData', state.actCode);
      commit('setActData', actData);
      // 初始弹窗 check
      dispatch('initModalCheck', actData);
    },
    async getShareCode({ state }) {
      try {
        const shareCode = await fetchShareCode({
          code: state.actCode,
          activityId: state.actData.id
        });
        return shareCode;
      } catch (error) {
        Vue.prototype.$hll.toast(error && error.msg);
        throw error;
      }
    },
    getWxaQRCode(_, data) {
      return getXlWxaQRCode(data);
    },
    async launchInviteTask({ dispatch, state }) {
      Vue.prototype.$hll.showLoading({});
      try {
        await addInviteTask({
          actId: state.actData.id
        });
      } catch (error) {
        throw error;
      } finally {
        Vue.prototype.$hll.hideLoading();
      }
      await dispatch('refreshTaskInfo');
    },
    setTitle(_, tplData) {
      const actName = tplData && tplData.pageConfig && tplData.pageConfig.name;
      if (actName) {
        document.title = actName;
      }
    }
  },
  mutations: {
    setTplData(state, val) {
      state.tplData = val;
    },
    setActData(state, val) {
      state.actData = val;
    },
    setComponentObjs(
      state,
      { pinnedBtnComponentObjs, flowComponentObjs, positionComponentObjs }
    ) {
      state.pinnedBtnComponentObjs = pinnedBtnComponentObjs;
      state.flowComponentObjs = flowComponentObjs;
      state.positionComponentObjs = positionComponentObjs;
    },
    toggleTaskModal(state, val) {
      state.isShowTaskModal = val;
    },
    setModal(state, val) {
      state.modalConfig = val;
    },
    toggleCouponDetail(state, val) {
      state.isShowCouponDetail = val;
    },
    initSetActCode(state, actCode) {
      state.actCode = actCode;
    },
    toggleWinModal(state, val) {
      state.isShowWinModal = val;
    },
    toggleFailModal(state, val) {
      state.isShowFailModal = val;
    },
    setMetaInfo(state, val) {
      state.metaInfo = val;
    }
  }
};

/**
 * 受邀人
 */
const inviteeStore = {
  namespaced: true,
  state() {
    return {
      actData: null,
      tplData: null,
      actCode: null,
      metaInfo: null,
      pinnedBtnComponentObjs: [],
      flowComponentObjs: [],
      positionComponentObjs: [],
      isShowCouponDetail: false,
      invitationCode: null
    };
  },
  actions: {
    async fetchData(_, invitationCode) {
      Vue.prototype.$hll.showLoading({});
      let actData;
      try {
        actData = await getInviteePageInfo({ invitationCode });
        if (!actData) {
          throw { msg: '发生错误' };
        }
      } catch (error) {
        throw error;
      } finally {
        Vue.prototype.$hll.hideLoading();
      }
      return actData;
    },
    parseTplData(_, actData) {
      let metaInfo = {};
      try {
        metaInfo = JSON.parse(actData.metaInfo);
      } catch (e) {
        console.error('模板数据 parse 出错');
      }
      return metaInfo;
    },
    setPage({ commit }, metaInfo) {
      const inviteePage = metaInfo.pages.find(pageObj => {
        return pageObj.pageName === 'invitee';
      });
      commit('setTplData', inviteePage);
      return inviteePage;
    },
    resolveComponents({ commit }, { actData, inviteePage }) {
      // 解析组件
      const components = inviteePage.components;
      if (!components || !components.length) {
        return;
      }
      // 拿到 metaInfo 之后直接调 helper.resolveComponents 即可生成组件数组用于渲染
      const [
        pinnedBtnComponentObjs, // pinnedBtnComponentObjs 就是右上角固定按钮组，比较特殊，需要单独提取
        flowComponentObjs, // 流式布局的组件数组
        positionComponentObjs // 固定定位的组件数组
      ] = resolveComponents(components, { actData, actComponentLib });
      // 特殊处理助力组件
      const resolvedFlowComponentObjs = flowComponentObjs.map(obj => {
        if (obj.componentTypeId === 1302) {
          return {
            ...obj,
            fullWidth: true
          };
        }
        return obj;
      });
      commit('setComponentObjs', {
        pinnedBtnComponentObjs,
        flowComponentObjs: resolvedFlowComponentObjs,
        positionComponentObjs
      });
      return {
        pinnedBtnComponentObjs,
        flowComponentObjs,
        positionComponentObjs
      };
    },
    statusCheck(_, actData) {
      const currentTask = actData && actData.inviterTaskInfo;
      if (!currentTask) {
        return;
      }
      if (
        actData.inviteeHasTask === 'thisHasHelp' ||
        actData.inviteeHasTask === 'lastHasHelp'
      ) {
        Vue.prototype.$hll.toast('你已经帮我助力过啦～');
        return;
      }
    },
    setDefaultAvatar({ commit }, flowComponentObjs) {
      const actComponent = flowComponentObjs.find(obj => {
        return obj.componentTypeId === 1302;
      });
      if (actComponent) {
        commit(
          'app/inviteEarnCoupon/playHistory/setDefaultAvatar',
          {
            defaultAvatarImage: actComponent.componentDetail.defaultAvatarImage
          },
          { root: true }
        );
      }
    },
    async getActData({ commit, dispatch }, invitationCode) {
      // 获取数据
      const actData = await dispatch('fetchData', invitationCode);
      commit('setActData', actData);
      commit('app/inviteEarnCoupon/playHistory/setActId', actData.id, {
        root: true
      });
      // 解析 metaInfo
      const metaInfo = await dispatch('parseTplData', actData);
      commit('setMetaInfo', metaInfo);
      // 解析 invitee page
      const inviteePage = await dispatch('setPage', metaInfo);
      commit('setTplData', inviteePage);
      // 设置活动规则
      commit('app/inviteEarnCoupon/setRuleContent', actData.activityRule, {
        root: true
      });
      // 解析组件
      const { flowComponentObjs } = await dispatch('resolveComponents', {
        actData,
        inviteePage
      });
      // 设置默认头像（活动记录页面用）
      dispatch('setDefaultAvatar', flowComponentObjs);
      // 设置title
      dispatch('setTitle', inviteePage);
      // 是否已助力过
      dispatch('statusCheck', actData);
    },
    async refreshTaskInfo({ dispatch, commit, state }) {
      // 获取数据
      const actData = await dispatch('fetchData', state.invitationCode);
      commit('setActData', actData);
    },
    // 帮他助力api
    async addInviteeTask(_, invitationCode) {
      Vue.prototype.$hll.showLoading({});
      let actData;
      try {
        actData = await addInviteeTask({ invitationCode });
      } catch (error) {
        if (isRiskError(error.ret)) {
          Vue.prototype.$hll.toast('您的账号异常，无法助力');
          return;
        }
        Vue.prototype.$hll.toast((error && error.msg) || '助力失败');
        console.error(error);
      } finally {
        Vue.prototype.$hll.hideLoading();
      }
      return actData;
    },
    // 帮他助力
    async acceptTask({ commit, dispatch, state }) {
      const actData = await dispatch('addInviteeTask', state.invitationCode);
      if (actData) {
        commit('setActData', actData);
        if (actData.result === 'SUCCESS') {
          Vue.prototype.$hll.toast('助力成功');
          return actData;
        }
      }
    },
    setTitle(_, tplData) {
      const actName = tplData && tplData.pageConfig && tplData.pageConfig.name;
      if (actName) {
        document.title = actName;
      }
    }
  },
  mutations: {
    setTplData(state, val) {
      state.tplData = val;
    },
    setActData(state, val) {
      state.actData = val;
    },
    setComponentObjs(
      state,
      { pinnedBtnComponentObjs, flowComponentObjs, positionComponentObjs }
    ) {
      state.pinnedBtnComponentObjs = pinnedBtnComponentObjs;
      state.flowComponentObjs = flowComponentObjs;
      state.positionComponentObjs = positionComponentObjs;
    },
    toggleCouponDetail(state, val) {
      state.isShowCouponDetail = val;
    },
    initSetActCode(state, actCode) {
      state.actCode = actCode;
    },
    initSetInvitationCode(state, val) {
      state.invitationCode = val;
    },
    setMetaInfo(state, val) {
      state.metaInfo = val;
    }
  }
};

/**
 * 活动记录
 */
const playHistoryStore = {
  namespaced: true,
  state() {
    return {
      joinedRecords: [],
      launchedRecords: [],
      currentActiveTabId: '',
      defaultAvatarImage: null,
      actId: null
    };
  },
  actions: {
    async getPlayHistory({ commit, state }) {
      Vue.prototype.$hll.showLoading({});
      try {
        const recordsData = await getActivityTaskLog({ actId: state.actId });
        await commit('setRecordsData', recordsData);
      } catch (error) {
        throw error;
      } finally {
        Vue.prototype.$hll.hideLoading();
      }
    }
  },
  mutations: {
    setActiveTab(state, val) {
      state.currentActiveTabId = val;
    },
    setRecordsData(state, data) {
      state.joinedRecords = data.inviteeTaskInfoLog;
      state.launchedRecords = data.inviterTaskInfoLog;
    },
    setDefaultAvatar(state, { defaultAvatarImage }) {
      state.defaultAvatarImage = defaultAvatarImage;
    },
    setActId(state, val) {
      state.actId = val;
    }
  }
};

export default {
  namespaced: true,
  state() {
    return {
      activityRule: null
    };
  },
  actions: {},
  mutations: {
    setRuleContent(state, val) {
      state.activityRule = val;
    }
  },
  modules: {
    inviter: inviterStore,
    invitee: inviteeStore,
    playHistory: playHistoryStore
  }
};
